import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import AuthCallback from "@/pages/AuthCallback";
import { Toaster } from "@/components/ui/sonner";
import { TestSiteBanner } from "@/components/TestSiteBanner";
import { ThemeProvider } from "@/context/ThemeContext";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Shell from "@/components/Shell";
import { Spinner } from "@/components/kit";
import Landing from "@/pages/Landing";
import Pricing from "@/pages/Pricing";
import About from "@/pages/About";
import WellbeingCheck from "@/pages/WellbeingCheck";
import ToolsHub from "@/pages/ToolsHub";
import ToolPage from "@/pages/ToolPage";
import Embed from "@/pages/Embed";
import Contact from "@/pages/Contact";
import Legal from "@/pages/Legal";
import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import ConfirmEmail from "@/pages/ConfirmEmail";
import Paywall from "@/pages/Paywall";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PrivacyPromise from "@/pages/PrivacyPromise";
import PaymentCancel from "@/pages/PaymentCancel";
import SupporterView from "@/pages/SupporterView";
import Home from "@/pages/Home";
import CheckIn from "@/pages/CheckIn";
import CalendarPage from "@/pages/CalendarPage";
import AIChat from "@/pages/AIChat";
import Achievements from "@/pages/Achievements";
import SettingsPage from "@/pages/SettingsPage";
import Insights from "@/pages/Insights";
import Coping from "@/pages/Coping";
import Breathing from "@/pages/Breathing";
import Meditation from "@/pages/Meditation";
import Journal from "@/pages/Journal";
import Safety from "@/pages/Safety";
import Bingo from "@/pages/Bingo";
import Supporters from "@/pages/Supporters";
import Voice from "@/pages/Voice";
import CustomItems from "@/pages/CustomItems";
import Reminders from "@/pages/Reminders";
import Admin from "@/pages/Admin";
import Inbox from "@/pages/Inbox";
import ToughDayPlan from "@/pages/ToughDayPlan";
import WatchVideo from "@/pages/WatchVideo";
import Checkout from "@/pages/Checkout";
import FinishSignup from "@/pages/FinishSignup";
import StopReminders from "@/pages/StopReminders";
import SavedQuotes from "@/pages/SavedQuotes";
import GuidesRead from "@/pages/GuidesRead";
import Garden from "@/pages/Garden";
import Hugs from "@/pages/Hugs";
import Bedtime from "@/pages/Bedtime";
import BodyMap from "@/pages/BodyMap";
import SafetyWalkthrough from "@/pages/SafetyWalkthrough";
import TherapistShare from "@/pages/TherapistShare";
import TherapistView from "@/pages/TherapistView";
import MoreAI from "@/pages/MoreAI";
import Letters from "@/pages/Letters";
import SOS from "@/pages/SOS";
import WorryBox from "@/pages/WorryBox";
import Reframe from "@/pages/Reframe";
import GratitudeJar from "@/pages/GratitudeJar";
import JoySnaps from "@/pages/JoySnaps";
import Playbook from "@/pages/Playbook";
import Articles from "@/pages/Articles";
import Article from "@/pages/Article";

const fromState = (from) => ({ from });

function Protected({ children, admin }) {
  const { user, loading } = useAuth();
  const loc = useLocation();
  if (loading) return <Spinner className="min-h-screen items-center" />;
  if (!user) return <Navigate to="/landing" replace state={fromState(loc.pathname)} />;
  if (admin && !user.is_admin) return <Navigate to="/" replace />;
  if (!admin && !user.has_paid) return <Navigate to="/paywall" replace />;
  return children;
}

function PublicOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <Spinner className="min-h-screen items-center" />;
  if (user?.has_paid) return <Navigate to="/" replace />;
  return children;
}

const InAppWellbeing = () => <WellbeingCheck inApp />;
const WellbeingEntry = () => {
  const { user, loading } = useAuth();
  if (loading) return <Spinner className="min-h-screen items-center" />;
  return user?.has_paid ? <Navigate to="/wellbeing" replace /> : <WellbeingCheck />;
};

const APP_ROUTES = [
  ["/wellbeing", InAppWellbeing], ["/", Home], ["/checkin", CheckIn], ["/calendar", CalendarPage], ["/ai", AIChat],
  ["/achievements", Achievements], ["/settings", SettingsPage], ["/insights", Insights],
  ["/coping", Coping], ["/breathing", Breathing], ["/meditation", Meditation], ["/journal", Journal],
  ["/safety", Safety], ["/bingo", Bingo], ["/supporters", Supporters], ["/voice", Voice],
  ["/custom-items", CustomItems], ["/reminders", Reminders], ["/contact", Contact], ["/inbox", Inbox],
  ["/plan", ToughDayPlan], ["/saved-quotes", SavedQuotes], ["/guides", GuidesRead], ["/garden", Garden], ["/letters", Letters], ["/worry-box", WorryBox], ["/reframe", Reframe], ["/gratitude", GratitudeJar], ["/joy-snaps", JoySnaps], ["/playbook", Playbook], ["/hugs", Hugs], ["/body-map", BodyMap], ["/therapist-share", TherapistShare], ["/more-ai", MoreAI],
];

function AppRouter() {
  const location = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [location.pathname]);
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/landing" element={<PublicOnly><Landing /></PublicOnly>} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/articles" element={<Articles />} />
      <Route path="/articles/:slug" element={<Article />} />
      <Route path="/about" element={<About />} />
      <Route path="/wellbeing-check" element={<WellbeingEntry />} />
      <Route path="/tools" element={<ToolsHub />} />
      <Route path="/tools/:slug" element={<ToolPage />} />
      <Route path="/embed" element={<Embed />} />
      <Route path="/t/:token" element={<TherapistView />} />
      <Route path="/legal" element={<Legal />} />
      <Route path="/privacy-promise" element={<PrivacyPromise />} />
      <Route path="/contact-us" element={<Contact standalone />} />
      <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
      <Route path="/signup" element={<PublicOnly><Login signup /></PublicOnly>} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/confirm-email" element={<ConfirmEmail />} />
      <Route path="/paywall" element={<Paywall />} />
      <Route path="/checkout" element={<Checkout />} />
      <Route path="/finish-signup" element={<FinishSignup />} />
      <Route path="/stop-reminders" element={<StopReminders />} />
      <Route path="/payment-success" element={<PaymentSuccess />} />
      <Route path="/payment-cancel" element={<PaymentCancel />} />
      <Route path="/supporter-view" element={<SupporterView />} />
      <Route path="/watch" element={<WatchVideo />} />
      <Route path="/sos" element={<Protected><SOS /></Protected>} />
      <Route path="/bedtime" element={<Protected><Bedtime /></Protected>} />
      <Route path="/safety-plan-now" element={<Protected><SafetyWalkthrough /></Protected>} />
      <Route element={<Protected><Shell /></Protected>}>
        {APP_ROUTES.map(([p, C]) => <Route key={p} path={p} element={<C />} />)}
      </Route>
      <Route element={<Protected admin><Shell /></Protected>}>
        <Route path="/admin" element={<Admin />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <TestSiteBanner />
        <BrowserRouter>
          <AppRouter />
          <Toaster position="bottom-right" richColors />
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
